import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUEat } from '@/contexts/UEatContext';
import { ShoppingCart, Menu, X, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const LOGO_URL = '/assets/images/logos/xy43zlm2_logo-belgo-831e91aa.avif';

export default function Header() {
  const { t, lang, toggleLang } = useLanguage();
  const { itemCount } = useCart();
  const { user } = useAuth();
  const ueat = useUEat();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return null;

  const menuToUEat = !!(ueat.enabled && ueat.url && ueat.redirect_menu_link);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: menuToUEat ? ueat.url : '/menu', label: t('nav.menu'), external: menuToUEat },
    { to: '/assets/files/pdf/cutp7udy_Belgo_menu-traiteur_2026-a9fa5ffe.pdf', label: t('nav.catering'), external: true },
    { to: '/assets/files/pdf/22z8rqf9_Belgo_menu-foodtruck_2026-80234699.pdf', label: t('nav.foodtruck'), external: true },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/about', label: t('nav.about') },
    { to: '/jobs', label: lang === 'fr' ? 'Emplois' : 'Jobs' },
    { to: '/retailers', label: lang === 'fr' ? 'Points de vente' : 'Retailers' },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <header className="glass-header fixed w-full top-0 z-50 shadow-lg" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Resto Belgo" className="h-12 lg:h-14 w-auto object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map(link => link.external ? (
              <a
                key={link.to}
                href={link.to}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-gray-300 hover:text-white hover:bg-white/10"
                data-testid={`nav-${link.label}`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.to
                    ? 'bg-[#e53a3a] text-white'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Lang Toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{lang === 'fr' ? 'EN' : 'FR'}</span>
            </button>

            {/* Cart */}
            <Link to="/cart" className="relative p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <Badge className="absolute -top-1 -right-1 text-xs px-1.5 py-0.5 bg-[#e53a3a] text-white border-0">
                  {itemCount}
                </Badge>
              )}
            </Link>

            {/* Order CTA */}
            <a
              href="https://www.skipthedishes.com/belgio-resto-et-traiteur"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex btn-belgo px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#e53a3a] hover:bg-[#c62828] transition-colors"
              data-testid="delivery-cta"
            >
              {lang === 'fr' ? 'Livraison' : 'Delivery'}
            </a>
            {ueat.enabled && ueat.url ? (
              <a
                href={ueat.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex btn-belgo px-4 py-2 rounded-lg text-sm font-bold text-black bg-[#f4d00d] hover:bg-[#d4b800] transition-colors items-center gap-1"
                data-testid="order-cta-ueat"
              >
                {lang === 'fr' ? 'POUR EMPORTER' : 'FOR TAKEOUT'}
              </a>
            ) : (
              <Link
                to="/menu"
                className="hidden sm:inline-flex btn-belgo px-4 py-2 rounded-lg text-sm font-bold text-black bg-[#f4d00d] hover:bg-[#d4b800] transition-colors"
                data-testid="order-cta"
              >
                {lang === 'fr' ? 'POUR EMPORTER' : 'FOR TAKEOUT'}
              </Link>
            )}

            {/* Admin link */}
            {user && (user.role === 'admin' || user.role === 'superadmin') && (
              <Link to="/admin" className="text-xs px-2 py-1 rounded bg-[#e53a3a] text-white font-semibold" data-testid="admin-link">
                {t('nav.admin')}
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-white/10" data-testid="mobile-nav">
            <nav className="flex flex-col gap-1 pt-2">
              {navLinks.map(link => link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-medium transition-colors text-gray-300 hover:text-white hover:bg-white/10"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.to ? 'bg-[#e53a3a] text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
